/* Renerded's progressive Shopify enhancement. Products, prices and inventory
   come from Liquid; the shopping bag is always the real Shopify session cart. */
(function () {
  'use strict';
  if (window.RenerdedRuntime) return;
  window.RenerdedRuntime = true;

  const config = window.RenerdedTheme || {};
  const root = String(config.root || (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/').replace(/\/?$/, '/');
  const cartUrl = config.cartUrl || root + 'cart';
  const keys = { theme: 'renerded-theme-mode-v1', saved: 'renerded-theme-saved-v1' };
  const products = new Map();
  const controllers = new WeakMap();
  const originalOrder = new WeakMap();
  const dialogFocus = new WeakMap();
  let cart = null;
  let cartCurrency = config.currency || 'USD';
  let cartReadSequence = 0;
  let mutationQueue = Promise.resolve();
  let pendingMutations = 0;
  let category = 'all';
  let savedOnly = false;
  let quickController = null;
  let imageView = 'product';
  let toastTimer;

  const $ = id => document.getElementById(id);
  const escape = value => String(value == null ? '' : value).replace(/[&<>"']/g, character => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[character]));
  const pixel = name => '<svg class="pixel-icon" width="16" height="16" viewBox="0 0 16 16" aria-hidden="true" focusable="false"><use href="#pixel-' + escape(name) + '"></use></svg>';
  const readSaved = () => {
    try {
      const value = JSON.parse(localStorage.getItem(keys.saved) || '[]');
      return new Set(Array.isArray(value) ? value.filter(item => typeof item === 'string' && item.length < 256).slice(0, 500) : []);
    } catch (_) { return new Set(); }
  };
  const saved = readSaved();

  function money(cents, currency) {
    const amount = Number(cents);
    if (!Number.isFinite(amount)) return '';
    try {
      return new Intl.NumberFormat(config.locale || document.documentElement.lang || 'en', { style: 'currency', currency: currency || cartCurrency }).format(amount / 100);
    } catch (_) { return (amount / 100).toFixed(2) + ' ' + String(currency || cartCurrency); }
  }

  function plainText(html) {
    const parsed = new DOMParser().parseFromString(String(html || ''), 'text/html');
    parsed.querySelectorAll('script,style,iframe,object,embed').forEach(element => element.remove());
    return (parsed.body.textContent || '').replace(/\s+/g, ' ').trim();
  }

  function safeUrl(value) {
    if (!value) return '';
    try {
      const url = new URL(String(value), location.href);
      return /^https?:$/.test(url.protocol) ? url.href : '';
    } catch (_) { return ''; }
  }

  function imageUrl(value) {
    if (typeof value === 'string') return safeUrl(value);
    return safeUrl(value && (value.src || value.url || (value.preview_image && value.preview_image.src)));
  }

  function setText(id, text) { if ($(id)) $(id).textContent = text; }

  function toast(message) {
    const element = $('toast');
    if (!element) return;
    element.textContent = message;
    element.classList.add('visible');
    clearTimeout(toastTimer);
    toastTimer = setTimeout(() => element.classList.remove('visible'), 4200);
  }

  function applyTheme(value) {
    const theme = value === 'light' ? 'light' : 'dark';
    document.documentElement.dataset.theme = theme;
    const button = $('theme-toggle');
    if (button) {
      button.setAttribute('aria-label', 'Switch to ' + (theme === 'light' ? 'dark' : 'light') + ' mode');
      button.setAttribute('aria-pressed', String(theme === 'light'));
      const label = button.querySelector('.theme-label');
      const icon = button.querySelector('.theme-icon');
      if (label) label.textContent = theme === 'light' ? 'Dark mode' : 'Light mode';
      if (icon) icon.innerHTML = pixel(theme === 'light' ? 'moon' : 'sun');
    }
    const meta = document.querySelector('meta[name="theme-color"]');
    if (meta) meta.content = theme === 'light' ? '#f5f6f4' : '#0a0a0f';
  }

  function parseProducts(scope) {
    const scripts = Array.from(scope.querySelectorAll('script[data-product-json]'));
    if (scope.matches && scope.matches('script[data-product-json]')) scripts.unshift(scope);
    scripts.forEach(script => {
      try {
        const payload = JSON.parse(script.textContent);
        const product = payload.product || payload;
        const handle = product.handle || script.dataset.productHandle;
        if (!handle || !Array.isArray(product.variants)) return;
        products.set(handle, Object.assign({}, payload, { product, handle }));
      } catch (_) { /* A malformed optional quick-view payload never breaks native links. */ }
    });
  }

  function productOptions(product) {
    return (product.options || []).map((option, index) => ({
      name: typeof option === 'string' ? option : option.name || 'Option ' + (index + 1),
      values: Array.from(new Set(product.variants.map(variant => variant.options ? variant.options[index] : variant['option' + (index + 1)]).filter(value => value != null)))
    }));
  }

  function variantOptions(variant) {
    return variant.options || [variant.option1, variant.option2, variant.option3].filter(value => value != null);
  }

  function formPayload(form) {
    const handle = form.dataset.productHandle || (form.closest('[data-product-handle]') || {}).dataset?.productHandle;
    return products.get(handle);
  }

  function makeController(form, payload, quick) {
    if (!form || !payload) return null;
    const existing = controllers.get(form);
    if (existing && existing.payload.handle === payload.handle) return existing;
    const product = payload.product;
    const select = form.querySelector('select[name="id"]');
    if (!select) return null;
    if (quick) {
      select.innerHTML = product.variants.map(variant => '<option value="' + escape(variant.id) + '"' + (!variant.available ? ' data-sold-out="true"' : '') + '>' + escape(variant.title || 'Default Title') + ' — ' + escape(money(variant.price)) + (!variant.available ? ' — Sold out' : '') + '</option>').join('');
      const preferred = product.variants.find(variant => variant.available) || product.variants[0];
      if (preferred) select.value = String(preferred.id);
    }
    const selected = product.variants.find(variant => String(variant.id) === select.value) || product.variants.find(variant => variant.available) || product.variants[0];
    const controller = { form, payload, product, select, quick, selected, options: productOptions(product), submitting: false };
    controllers.set(form, controller);
    if (selected) select.value = String(selected.id);
    buildVariantControls(controller);
    updateVariant(controller, selected, false);
    return controller;
  }

  function buildVariantControls(controller) {
    const area = controller.form.querySelector('[data-variant-controls]');
    if (!area) return;
    const onlyDefault = controller.product.variants.length === 1 && controller.product.variants[0].title === 'Default Title';
    area.hidden = onlyDefault;
    area.innerHTML = onlyDefault ? '' : controller.options.map((option, index) => '<fieldset class="variant-group"><legend>' + escape(option.name) + ' <span data-selected-option="' + index + '"></span></legend><div class="variant-options">' + option.values.map(value => '<button type="button" class="variant-option" data-option-index="' + index + '" data-option-value="' + escape(value) + '" aria-pressed="false">' + escape(value) + '</button>').join('') + '</div></fieldset>').join('');
  }

  function updateVariant(controller, variant, updateUrl) {
    controller.selected = variant || null;
    const scope = controller.quick ? $('product-dialog') : controller.form.closest('.native-product') || controller.form;
    const values = variant ? variantOptions(variant) : [];
    if (variant) controller.select.value = String(variant.id);
    controller.form.querySelectorAll('[data-option-index]').forEach(button => {
      const index = Number(button.dataset.optionIndex);
      const value = button.dataset.optionValue;
      const candidates = controller.product.variants.filter(candidate => {
        const options = variantOptions(candidate);
        return options[index] === value && options.slice(0, index).every((option, prior) => option === values[prior]);
      });
      button.disabled = candidates.length === 0;
      button.classList.toggle('is-unavailable', !candidates.some(candidate => candidate.available));
      button.setAttribute('aria-pressed', String(values[index] === value));
      button.title = candidates.length && !candidates.some(candidate => candidate.available) ? value + ' — sold out in this combination' : value;
    });
    controller.form.querySelectorAll('[data-selected-option]').forEach(label => { label.textContent = values[Number(label.dataset.selectedOption)] || ''; });
    const available = Boolean(variant && variant.available);
    const requiresPlan = controller.quick && controller.product.requires_selling_plan;
    const add = controller.form.querySelector('[data-add-to-cart]');
    if (add) {
      add.disabled = !available || controller.submitting || requiresPlan;
      add.setAttribute('aria-disabled', String(add.disabled));
      add.innerHTML = (controller.submitting ? 'Adding…' : requiresPlan ? 'Choose a plan on the product page' : available ? 'Add to bag' : variant ? 'Sold out' : 'Unavailable') + ' <span class="pixel-action" aria-hidden="true">' + pixel('arrow') + '</span>';
    }
    if (scope) {
      scope.querySelectorAll('[data-product-price]').forEach(element => { element.textContent = variant ? money(variant.price) : 'Unavailable'; });
      scope.querySelectorAll('[data-product-status]').forEach(element => { element.textContent = available ? 'Available' : variant ? 'Sold out' : 'This combination is unavailable'; });
      scope.querySelectorAll('[data-product-compare-price]').forEach(element => {
        const show = variant && Number(variant.compare_at_price) > Number(variant.price);
        element.hidden = !show;
        element.textContent = show ? money(variant.compare_at_price) : '';
      });
      if (controller.quick) {
        setText('product-price', variant ? money(variant.price) : 'Unavailable');
        setText('product-status', requiresPlan ? 'Visit the product page to choose a purchase plan.' : available ? 'Available' : variant ? 'Sold out' : 'This combination is unavailable');
        renderQuickStage();
      } else {
        const url = variant && imageUrl(variant.featured_image || variant.featured_media || controller.product.featured_image || (controller.product.images && controller.product.images[0]));
        if (url) scope.querySelectorAll('[data-product-media]').forEach(element => {
          const image = element.tagName === 'IMG' ? element : element.querySelector('img');
          if (image) { image.src = url; image.removeAttribute('srcset'); }
        });
        else if (controller.payload.fallbackArt || (config.fallbacks && config.fallbacks[controller.payload.handle])) {
          // Change only our explicitly illustrative garment, never catalog media.
          const colorIndex = controller.options.findIndex(option => /colou?r/i.test(option.name));
          const color = colorIndex >= 0 ? values[colorIndex] || '' : '';
          scope.querySelectorAll('[data-product-media] .visual-tee').forEach(visual => { visual.dataset.color = /black|charcoal|dark|navy/i.test(color) ? 'dark' : 'cream'; });
        }
      }
    }
    if (updateUrl && !controller.quick && variant && history.replaceState) {
      const url = new URL(location.href);
      url.searchParams.set('variant', variant.id);
      history.replaceState({}, '', url);
    }
    controller.form.dispatchEvent(new CustomEvent('renerded:variant-change', { bubbles: true, detail: { variant } }));
  }

  function chooseOption(controller, index, value) {
    if (!controller) return;
    const current = controller.selected ? variantOptions(controller.selected).slice() : [];
    current[index] = value;
    let candidates = controller.product.variants.filter(variant => variantOptions(variant)[index] === value);
    const exact = candidates.find(variant => variantOptions(variant).every((option, optionIndex) => option === current[optionIndex]));
    if (exact) { updateVariant(controller, exact, true); return; }
    const prefix = candidates.filter(variant => variantOptions(variant).slice(0, index).every((option, optionIndex) => option === current[optionIndex]));
    if (prefix.length) candidates = prefix;
    const variant = candidates.find(candidate => candidate.available) || candidates[0];
    updateVariant(controller, variant, true);
  }

  function setFormError(form, message) {
    let area = form.querySelector('[data-product-error],[data-product-form-status]');
    if (!area) {
      area = document.createElement('p');
      area.dataset.productError = '';
      area.className = 'form-error';
      area.setAttribute('role', 'alert');
      form.append(area);
    }
    area.setAttribute('role', 'alert');
    area.textContent = message || '';
    area.hidden = !message;
    if (message) {
      const link = document.createElement('a');
      link.href = cartUrl;
      link.textContent = ' View shopping bag';
      area.append(link);
    }
  }

  function showDialog(dialog) {
    if (!dialog || typeof dialog.showModal !== 'function') return false;
    dialogFocus.set(dialog, document.activeElement);
    document.querySelectorAll('dialog[open]').forEach(other => { if (other !== dialog) other.close(); });
    if (!dialog.open) dialog.showModal();
    document.body.classList.add('modal-open');
    return true;
  }

  function closeDialog(dialog) { if (dialog && dialog.open) dialog.close(); }

  function renderQuickStage() {
    if (!quickController || !$('product-stage-visual')) return;
    const payload = quickController.payload;
    const variant = quickController.selected;
    const product = payload.product;
    const actual = imageUrl((variant && (variant.featured_image || variant.featured_media)) || product.featured_image || (product.images && product.images[0]));
    const art = imageUrl(payload.fallbackArt || (config.fallbacks && config.fallbacks[payload.handle]));
    const src = imageView === 'art' && art ? art : actual || art;
    const alt = product.title + (imageView === 'art' ? ' artwork' : ' product image');
    const fallback = !actual && payload.format === 'tee';
    const colorIndex = quickController.options.findIndex(option => /colou?r/i.test(option.name));
    const color = variant && colorIndex >= 0 ? variantOptions(variant)[colorIndex] || '' : '';
    const garment = /black|charcoal|dark|navy/i.test(color) ? 'dark' : 'cream';
    if (src) {
      $('product-stage-visual').innerHTML = fallback && imageView === 'product' ? '<div class="visual-tee" data-color="' + garment + '"><div class="tee-shape"><img class="artwork-image" src="' + escape(src) + '" alt="' + escape(alt) + '"></div></div>' : '<div class="visual-art"><img class="artwork-image" src="' + escape(src) + '" alt="' + escape(alt) + '" decoding="async"></div>';
    } else $('product-stage-visual').textContent = 'Product image coming soon';
    setText('product-image-note', imageView === 'art' && art ? 'Artwork reference. See product images and description for the final item.' : actual ? 'Product image supplied in the Shopify catalog.' : fallback ? 'Illustrative garment placement, not supplier product photography.' : 'Artwork reference; not supplier product photography.');
    document.querySelectorAll('[data-image-view]').forEach(button => {
      button.setAttribute('aria-pressed', String(button.dataset.imageView === imageView));
      if (button.dataset.imageView === 'art') button.hidden = !art;
    });
  }

  function openProduct(handle) {
    const payload = products.get(handle);
    const dialog = $('product-dialog');
    const form = $('quick-product-form');
    if (!payload || !dialog || !form || typeof dialog.showModal !== 'function') return false;
    const product = payload.product;
    form.dataset.productHandle = handle;
    form.action = root + 'cart/add';
    setText('product-type', product.type || (payload.format === 'tee' ? 'Graphic tee' : payload.format === 'print' ? 'Art print' : 'Other Worlds'));
    setText('product-title', product.title);
    setText('product-description', payload.shortDescription || plainText(product.description));
    const quantity = form.querySelector('[name="quantity"]');
    if (quantity) quantity.value = '1';
    const link = $('sister-product');
    if (link) {
      const url = safeUrl(payload.url || root + 'products/' + encodeURIComponent(handle));
      if (link.tagName === 'A') link.href = url;
      else link.dataset.productUrl = url;
      link.textContent = 'Full product details ↗';
    }
    imageView = 'product';
    quickController = makeController(form, payload, true);
    if (!quickController) return false;
    setFormError(form, '');
    renderQuickStage();
    showDialog(dialog);
    dialog.scrollTop = 0;
    return true;
  }

  function renderSaved() {
    document.querySelectorAll('[data-save]').forEach(button => {
      const handle = button.dataset.save;
      const active = saved.has(handle);
      const title = products.get(handle)?.product.title || handle;
      button.setAttribute('aria-pressed', String(active));
      button.setAttribute('aria-label', (active ? 'Unsave ' : 'Save ') + title);
      button.innerHTML = pixel(active ? 'heart-filled' : 'heart');
    });
    setText('saved-count', saved.size);
    const toggle = $('favorites-toggle');
    if (toggle) toggle.setAttribute('aria-pressed', String(savedOnly));
  }

  function filterProducts() {
    const grid = $('product-grid');
    if (!grid) { renderSaved(); return; }
    const cards = Array.from(grid.querySelectorAll('.product-card[data-product-handle]'));
    cards.forEach((card, index) => { if (!originalOrder.has(card)) originalOrder.set(card, index); });
    const query = ($('search')?.value || '').trim().toLowerCase();
    const terms = query.split(/\s+/).filter(Boolean);
    const sort = $('sort')?.value || 'featured';
    const price = card => Number(card.dataset.price || products.get(card.dataset.productHandle)?.product.price || 0);
    cards.sort((a, b) => sort === 'price-low' ? price(a) - price(b) : sort === 'price-high' ? price(b) - price(a) : sort === 'name' ? (a.dataset.title || a.querySelector('.product-title')?.textContent || '').localeCompare(b.dataset.title || b.querySelector('.product-title')?.textContent || '') : originalOrder.get(a) - originalOrder.get(b));
    let count = 0;
    cards.forEach(card => {
      const product = products.get(card.dataset.productHandle)?.product;
      // Do not search the embedded application/json script (variant metadata can
      // otherwise create false matches unrelated to the card a visitor sees).
      const haystack = (card.dataset.search || [card.dataset.title || card.dataset.name, product?.title, product?.type, ...(product?.tags || []), card.querySelector('.product-design')?.textContent].filter(Boolean).join(' ')).toLowerCase();
      const visible = (category === 'all' || card.dataset.format === category) && (!savedOnly || saved.has(card.dataset.productHandle)) && terms.every(term => haystack.includes(term));
      card.hidden = !visible;
      if (visible) count++;
      grid.append(card);
    });
    if ($('empty-state')) $('empty-state').hidden = count > 0;
    setText('results-count', count + ' / ' + cards.length + ' PIECES');
    document.querySelectorAll('#category-filters [data-category]').forEach(button => {
      const active = button.dataset.category === category;
      button.classList.toggle('active', active);
      button.setAttribute('aria-pressed', String(active));
    });
    renderSaved();
  }

  function setCategory(value) {
    category = ['all', 'tee', 'print'].includes(value) ? value : 'all';
    filterProducts();
  }

  function cartError(message) {
    const area = $('cart-error');
    if (!area) { if (message) toast(message); return; }
    area.textContent = message || '';
    area.hidden = !message;
    area.setAttribute('role', 'alert');
    if (message) {
      const link = document.createElement('a');
      link.href = cartUrl;
      link.textContent = ' View full shopping bag';
      area.append(link);
    }
  }

  function setCartBusy() {
    const busy = pendingMutations > 0;
    const dialog = $('cart-dialog');
    if (dialog) {
      dialog.setAttribute('aria-busy', String(busy));
      dialog.querySelectorAll('[data-cart-quantity], button[name="checkout"]').forEach(button => { button.disabled = busy || (button.name === 'checkout' && (!cart || !cart.item_count)); });
    }
  }

  function renderCart(value) {
    if (!value || !Array.isArray(value.items) || typeof value.item_count !== 'number') throw new Error('Shopify returned an unexpected shopping bag response.');
    cart = value;
    cartCurrency = value.currency || config.currency || cartCurrency;
    const count = Number(value.item_count) || 0;
    document.querySelectorAll('.cart-count').forEach(element => { element.textContent = count; });
    setText('cart-heading-count', count);
    if ($('cart-open')) $('cart-open').setAttribute('aria-label', 'Open shopping bag, ' + count + (count === 1 ? ' item' : ' items'));
    if ($('cart-empty')) $('cart-empty').hidden = count > 0;
    if ($('cart-summary')) $('cart-summary').hidden = !count;
    if ($('cart-items')) $('cart-items').innerHTML = (value.items || []).map(item => {
      const title = item.product_title || item.title || 'Product';
      const url = safeUrl(item.url || cartUrl);
      const src = imageUrl(item.featured_image || item.image);
      const unit = item.final_price == null ? item.price : item.final_price;
      const line = item.final_line_price == null ? item.line_price : item.final_line_price;
      const options = (item.options_with_values || []).filter(option => option.value !== 'Default Title').map(option => option.name + ': ' + option.value).join(' / ');
      const properties = Object.entries(item.properties || {}).filter(([key, property]) => !key.startsWith('_') && property != null && property !== '').map(([key, property]) => '<p>' + escape(key) + ': ' + escape(property) + '</p>').join('');
      const discounts = (item.line_level_discount_allocations || []).map(allocation => '<p class="cart-discount">' + escape(allocation.discount_application?.title || 'Discount') + ' −' + escape(money(allocation.amount, cartCurrency)) + '</p>').join('');
      const plan = item.selling_plan_allocation?.selling_plan?.name;
      return '<article class="cart-item" data-cart-line="' + escape(item.key) + '">' + (src ? '<a class="placement-thumbnail" href="' + escape(url) + '"><img class="artwork-image" src="' + escape(src) + '" alt="' + escape(title) + '" loading="lazy"></a>' : '') + '<div class="cart-item-copy"><h3><a href="' + escape(url) + '">' + escape(title) + '</a></h3>' + (options ? '<p>' + escape(options) + '</p>' : '') + properties + (plan ? '<p>' + escape(plan) + '</p>' : '') + '<span class="cart-unit-price">' + escape(money(unit, cartCurrency)) + ' each</span>' + discounts + '<div class="cart-item-actions"><div class="cart-qty"><button type="button" data-cart-quantity="' + Math.max(0, Number(item.quantity) - 1) + '" data-line-key="' + escape(item.key) + '" aria-label="Decrease quantity of ' + escape(title) + '">−</button><span aria-label="Quantity">' + Number(item.quantity) + '</span><button type="button" data-cart-quantity="' + (Number(item.quantity) + 1) + '" data-line-key="' + escape(item.key) + '" aria-label="Increase quantity of ' + escape(title) + '">+</button></div><button type="button" class="cart-remove" data-cart-quantity="0" data-line-key="' + escape(item.key) + '">Remove</button></div></div><strong class="cart-line-price">' + escape(money(line, cartCurrency)) + '</strong></article>';
    }).join('');
    setText('cart-subtotal', money(value.items_subtotal_price == null ? value.total_price : value.items_subtotal_price, cartCurrency));
    setText('cart-total', money(value.total_price, cartCurrency));
    const discounts = $('cart-discounts');
    if (discounts) discounts.innerHTML = (value.cart_level_discount_applications || []).map(discount => '<div><dt>' + escape(discount.title || 'Discount') + '</dt><dd>−' + escape(money(discount.total_allocated_amount, cartCurrency)) + '</dd></div>').join('');
    setCartBusy();
    document.dispatchEvent(new CustomEvent('renerded:cart-updated', { detail: { cart: value } }));
  }

  async function requestJson(path, options) {
    let response;
    try { response = await fetch(root + path, Object.assign({ credentials: 'same-origin', headers: { Accept: 'application/json' } }, options || {})); }
    catch (_) {
      const error = new Error(options?.method === 'POST' ? 'We could not confirm whether that change reached Shopify. Check your shopping bag before trying again.' : 'Your shopping bag could not be loaded.');
      error.unknown = options?.method === 'POST';
      throw error;
    }
    let result;
    try { result = await response.json(); }
    catch (_) {
      const error = new Error(options?.method === 'POST' ? 'Shopify returned an unreadable response. Check your shopping bag before trying again.' : 'Your shopping bag could not be loaded.');
      error.unknown = options?.method === 'POST';
      throw error;
    }
    if (!response.ok) {
      const description = typeof result.description === 'string' ? result.description : typeof result.message === 'string' ? result.message : 'Shopify could not accept that change.';
      const error = new Error(description);
      error.status = response.status;
      throw error;
    }
    return result;
  }

  async function loadCart() {
    const sequence = ++cartReadSequence;
    const value = await requestJson('cart.js');
    if (!Array.isArray(value.items) || typeof value.item_count !== 'number') throw new Error('Shopify returned an unexpected shopping bag response.');
    if (sequence === cartReadSequence) renderCart(value);
    return value;
  }

  async function openCart() {
    if (!$('cart-dialog') || typeof $('cart-dialog').showModal !== 'function') { location.assign(cartUrl); return; }
    try {
      await mutationQueue;
      await loadCart();
      cartError('');
      showDialog($('cart-dialog'));
    } catch (_) { location.assign(cartUrl); }
  }

  function enqueueMutation(work) {
    pendingMutations++;
    setCartBusy();
    const run = mutationQueue.then(work, work);
    mutationQueue = run.catch(() => {}).finally(() => { pendingMutations--; setCartBusy(); });
    return run;
  }

  async function addProduct(controller, submitter) {
    if (!controller || controller.submitting || !controller.selected?.available || (controller.quick && controller.product.requires_selling_plan)) return;
    const quantity = Number(controller.form.querySelector('[name="quantity"]')?.value || 1);
    if (!Number.isInteger(quantity) || quantity < 1) { setFormError(controller.form, 'Choose a whole-number quantity of at least 1.'); return; }
    // Capture once: a visitor can change the modal while this request is in flight.
    const form = controller.form;
    const data = new FormData(form);
    if (submitter?.name) data.set(submitter.name, submitter.value);
    const title = controller.product.title;
    controller.submitting = true;
    setFormError(form, '');
    updateVariant(controller, controller.selected, false);
    try {
      await enqueueMutation(async () => {
        // Never automatically repeat a mutation, even after an uncertain network failure.
        await requestJson('cart/add.js', { method: 'POST', body: data });
        try { await loadCart(); }
        catch (_) {
          if (controllers.get(form) === controller) setFormError(form, 'The item was added, but the bag could not refresh. View your shopping bag to confirm the total.');
        }
      });
      toast(title + ' added to your bag.');
    } catch (error) {
      // A rejected add can still adjust an existing line to available stock.
      // Reconcile by reading, never by replaying the rejected mutation.
      try { await loadCart(); } catch (_) {}
      if (controllers.get(form) === controller) setFormError(form, error.message);
      toast(error.message);
    }
    finally {
      controller.submitting = false;
      if (controllers.get(form) === controller) updateVariant(controller, controller.selected, false);
    }
  }

  async function changeQuantity(key, quantity) {
    if (!key || !Number.isInteger(quantity) || quantity < 0 || pendingMutations) return;
    const focusTarget = document.activeElement;
    cartError('');
    try {
      await enqueueMutation(async () => {
        const value = await requestJson('cart/change.js', { method: 'POST', headers: { Accept: 'application/json', 'Content-Type': 'application/json' }, body: JSON.stringify({ id: key, quantity }) });
        cartReadSequence++;
        renderCart(value);
      });
      if (focusTarget && !document.contains(focusTarget)) {
        const next = Array.from(document.querySelectorAll('[data-line-key]')).find(button => button.dataset.lineKey === key);
        if (next) next.focus();
        else $('cart-dialog')?.querySelector('[data-close]')?.focus();
      }
    } catch (error) { cartError(error.message); }
  }

  function setup(scope) {
    parseProducts(scope);
    scope.querySelectorAll('form[data-product-form]').forEach(form => makeController(form, formPayload(form), false));
    scope.querySelectorAll('.js-only').forEach(element => {
      if (element.hasAttribute('data-open-product')) {
        element.hidden = !products.has(element.dataset.openProduct) || !$('product-dialog') || typeof $('product-dialog').showModal !== 'function';
      } else element.hidden = false;
    });
    scope.querySelectorAll('dialog').forEach(dialog => {
      if (dialog.dataset.renerdedBound) return;
      dialog.dataset.renerdedBound = 'true';
      dialog.addEventListener('close', () => {
        if (!document.querySelector('dialog[open]')) document.body.classList.remove('modal-open');
        const previous = dialogFocus.get(dialog);
        if (previous && document.contains(previous) && !document.querySelector('dialog[open]')) previous.focus();
      });
      dialog.addEventListener('click', event => {
        if (event.target !== dialog) return;
        const rect = dialog.getBoundingClientRect();
        if (event.clientX < rect.left || event.clientX > rect.right || event.clientY < rect.top || event.clientY > rect.bottom) closeDialog(dialog);
      });
    });
    applyTheme(document.documentElement.dataset.theme || config.defaultTheme);
    filterProducts();
  }

  document.addEventListener('click', event => {
    const target = event.target instanceof Element ? event.target : null;
    if (!target) return;
    const theme = target.closest('#theme-toggle');
    if (theme) {
      const value = document.documentElement.dataset.theme === 'dark' ? 'light' : 'dark';
      applyTheme(value);
      try { localStorage.setItem(keys.theme, value); } catch (_) {}
      return;
    }
    const close = target.closest('[data-close]');
    if (close) { closeDialog($(close.dataset.close) || close.closest('dialog')); return; }
    const save = target.closest('[data-save]');
    if (save) {
      event.preventDefault();
      if (saved.has(save.dataset.save)) saved.delete(save.dataset.save); else saved.add(save.dataset.save);
      try { localStorage.setItem(keys.saved, JSON.stringify(Array.from(saved))); } catch (_) { toast('Saved for this visit. Browser storage is unavailable.'); }
      filterProducts();
      return;
    }
    const open = target.closest('[data-open-product]');
    if (open && !event.metaKey && !event.ctrlKey && !event.shiftKey && !event.altKey && openProduct(open.dataset.openProduct)) { event.preventDefault(); return; }
    const option = target.closest('[data-option-index][data-option-value]');
    if (option) { chooseOption(controllers.get(option.closest('form')), Number(option.dataset.optionIndex), option.dataset.optionValue); return; }
    const image = target.closest('[data-image-view]');
    if (image) { imageView = image.dataset.imageView === 'art' ? 'art' : 'product'; renderQuickStage(); return; }
    const full = target.closest('#sister-product[data-product-url]');
    if (full) { location.assign(full.dataset.productUrl); return; }
    const bag = target.closest('#cart-open,#product-view-bag,[data-open-cart]');
    if (bag && !event.metaKey && !event.ctrlKey && !event.shiftKey && !event.altKey && typeof fetch === 'function') { event.preventDefault(); openCart(); return; }
    const cartQuantity = target.closest('[data-cart-quantity][data-line-key]');
    if (cartQuantity) { event.preventDefault(); changeQuantity(cartQuantity.dataset.lineKey, Number(cartQuantity.dataset.cartQuantity)); return; }
    const quantityButton = target.closest('[data-product-quantity],#product-qty-minus,#product-qty-plus');
    if (quantityButton) {
      const form = quantityButton.closest('form');
      const input = form?.querySelector('[name="quantity"]') || $('product-qty');
      if (input) {
        const delta = quantityButton.dataset.productQuantity ? Number(quantityButton.dataset.productQuantity) : quantityButton.id === 'product-qty-minus' ? -1 : 1;
        const max = Number(input.max) || Infinity;
        input.value = String(Math.max(Number(input.min) || 1, Math.min(max, (Number(input.value) || 1) + delta)));
        input.dispatchEvent(new Event('change', { bubbles: true }));
      }
      return;
    }
    const filter = target.closest('#category-filters [data-category]');
    if (filter) { setCategory(filter.dataset.category); return; }
    const navigation = target.closest('[data-nav-category]');
    if (navigation && $('product-grid')) {
      const href = new URL(navigation.href || location.href, location.href);
      if (href.pathname === location.pathname) {
        savedOnly = false;
        if ($('search')) $('search').value = '';
        setCategory(navigation.dataset.navCategory);
      }
      return;
    }
    if (target.closest('#favorites-toggle')) { savedOnly = !savedOnly; filterProducts(); return; }
    if (target.closest('#reset-filters')) {
      savedOnly = false;
      if ($('search')) $('search').value = '';
      if ($('sort')) $('sort').value = 'featured';
      setCategory('all');
    }
  });

  document.addEventListener('change', event => {
    const target = event.target;
    if (target.matches('[data-variant-select],select[name="id"]')) {
      const controller = controllers.get(target.closest('form'));
      if (controller) updateVariant(controller, controller.product.variants.find(variant => String(variant.id) === target.value), true);
    }
    if (target.id === 'sort') filterProducts();
  });
  document.addEventListener('input', event => { if (event.target.id === 'search') filterProducts(); });
  document.addEventListener('submit', event => {
    const form = event.target;
    if (!form.matches('form[data-product-form],form[data-quick-product-form]') || typeof fetch !== 'function') return;
    const controller = controllers.get(form);
    if (!controller) return; // Preserve native submission if enhancement data is unavailable.
    if (event.submitter?.name === 'checkout') return;
    event.preventDefault();
    addProduct(controller, event.submitter);
  });
  document.addEventListener('keydown', event => {
    if (event.key === '/' && !event.ctrlKey && !event.metaKey && !document.querySelector('dialog[open]') && !event.target.closest('input,textarea,select,[contenteditable="true"]') && $('search')) {
      event.preventDefault();
      $('search').focus();
    }
  });
  document.addEventListener('shopify:section:load', event => setup(event.target));
  document.addEventListener('shopify:section:unload', event => {
    if (event.target.querySelector('dialog[open]')) document.body.classList.remove('modal-open');
  });

  const initialCategory = new URL(location.href).searchParams.get('category');
  if (['tee', 'print'].includes(initialCategory)) category = initialCategory;
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', () => setup(document), { once: true });
  else setup(document);
})();
